import React, { useState, useRef, useEffect } from "react";
import { View, Text, TextInput, StyleSheet } from "react-native";
import { useTranslation } from 'react-i18next';
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";

type Props = {
  onValidationChange?: (valid: boolean) => void;
};

export default function PinInputStep({ onValidationChange }: Props) {
  const [pin, setPin] = useState(["", "", "", ""]);
  const { t } = useTranslation();
  const inputs = useRef<(TextInput | null)[]>([]);
  const originalPin = useSessionStore((s: any) => s.consumerPin);
  const [isMismatch, setIsMismatch] = useState(false);

  const handleChange = (text: string, index: number) => {
    if (/^\d*$/.test(text)) {
      const newPin = [...pin];
      newPin[index] = text;
      setPin(newPin);

      // Move to next box automatically
      if (text && index < 3) {
        inputs.current[index + 1]?.focus();
      }

      // Backspace handling
      if (!text && index > 0) {
        inputs.current[index - 1]?.focus();
      }
    }
  };

  useEffect(() => {
    const joined = pin.join("");
    const complete = joined.length === 4;
    const mismatch = complete && String(originalPin || "") !== joined;
    setIsMismatch(mismatch);
    if (onValidationChange) {
      onValidationChange(complete && !mismatch);
    }
  }, [pin, originalPin, onValidationChange]);

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("PIN_CONFIRM_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("PIN_CONFIRM_SUBTITLE")}</Text>

      <View style={styles.pinContainer}>
        {pin.map((digit, index) => (
          <TextInput
            key={index}
            ref={(el) => (inputs.current[index] = el)}
            style={styles.pinInput}
            keyboardType="number-pad"
            maxLength={1}
            secureTextEntry
            value={digit}
            onChangeText={(text) => handleChange(text, index)}
          />
        ))}
      </View>

      {isMismatch && (
        <Text style={styles.error}>pin not matching</Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    justifyContent: "flex-start",
    marginTop: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    marginBottom: 6,
    color: "#111827",
  },
  subtitle: {
    fontSize: 14,
    color: "#6B7280",
    marginBottom: 20,
  },
  pinContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  pinInput: {
    width: 60,
    height: 60,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 12,
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    backgroundColor: "#F9FAFB",
    marginRight: 24
  },
  error: {
    color: "red",
    fontSize: 12,
    marginTop: 8,
  },
});
